/**
 * The floating chat window body frame shared by the canvas and home/workspace
 * surfaces: nothing while messages fetch, then the message history OR the
 * empty state, with the composer at the bottom.
 *
 * IMPORTANT: `children` (the surface's MessageInput) is rendered
 * unconditionally at the bottom so AudioRecordingButton / useAudioRecording
 * never unmount while recording is active. An earlier design conditionally
 * swapped MessageInput ↔ ChatArea on `hasMessages`, which stopped the stream
 * tracks (via useAudioRecording's cleanup effect) the moment React Query
 * resolved its first fetch for a session that already had messages — making
 * recording appear to immediately stop and kick off transcription.
 */

import ChatArea from "@/components/ChatArea";
import type { MessageInputControlSet } from "@/constants/message-input";
import type { ReferencedEntity } from "@cohesive/api-client";
import type { ReactNode } from "react";

export function FloatingChatBody({
  isMessagesPending,
  hasMessages,
  controlSet,
  placeholder,
  referencedEntities,
  onSendSuccess,
  emptyState,
  children,
}: {
  isMessagesPending: boolean;
  hasMessages: boolean;
  controlSet: MessageInputControlSet;
  placeholder: string;
  /** Entities riding on the next send (canvas selections / file-record excerpts). */
  referencedEntities: ReferencedEntity[];
  /** Clears sent selections (mirrors the composer's own send path). */
  onSendSuccess: (entities: ReferencedEntity[] | undefined) => void;
  /** Shown when the session has no messages (see `FloatingChatEmptyState`). */
  emptyState: ReactNode;
  /** The surface's always-mounted MessageInput. */
  children: ReactNode;
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Upper area: nothing while fetching, then history OR the empty state. */}
      {isMessagesPending ? (
        <div className="flex-1 min-h-0" aria-busy />
      ) : hasMessages ? (
        // Full-bleed: the scroll area runs to the panel edges (scrollbar snug,
        // not inset). Horizontal inset lives on the message content + composer
        // instead, so messages, COT rail, and input share one 16px grid.
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full flex flex-col w-full scrollbar-hide">
            {/* hideMessageInput=true — the stable input below handles sending */}
            <ChatArea
              location="sidebar"
              controlSet={controlSet}
              placeholder={placeholder}
              className="!h-full"
              hideMessageInput={true}
              referencedEntities={referencedEntities}
              onSendSuccess={onSendSuccess}
            />
          </div>
        </div>
      ) : (
        emptyState
      )}

      {children}
    </div>
  );
}
